import { isPhrasingContent } from './dom/content';
import { findAncestor, findPreviousChildOrSibling, findPreviousSiblingOrAuncle } from './dom/node';
import {
  createPointAtEndOfNode,
  createPointWithEndOfRange,
  createPointWithOffsetInContainer,
  createPointWithStartOfRange,
  Point,
  setEndOfRangeWithPoint,
  updateSelectionWithPoints,
} from './dom/point';

export interface SelectionLike {
  readonly anchorNode: Node;
  readonly anchorOffset: number;
  readonly focusNode: Node;
  readonly focusOffset: number;
}

export interface NonNullSelection extends Selection, SelectionLike {
  readonly type: 'Caret' | 'Range';
  readonly anchorNode: Node;
  readonly focusNode: Node;
}

export interface PointSelection {
  readonly anchorPoint: Point;
  readonly focusPoint: Point;
}

export function isSelectionPresent(selection: Selection | null): selection is NonNullSelection {
  return Boolean(selection && selection.type !== 'None' && selection.anchorNode && selection.focusNode);
}

export function isSelectionCollapsed(selection: SelectionLike): boolean {
  if ('isCollapsed' in selection) {
    const domSelection = selection as Selection;
    return domSelection.isCollapsed;
  }

  return selection.anchorNode === selection.focusNode && selection.anchorOffset === selection.focusOffset;
}

export function isSelectionBackward(selection: Selection): boolean {
  return selection.rangeCount === 1 && selection.focusNode !== selection.getRangeAt(0).endContainer;
}

export function isSelectionContainedByNode(node: Node, selection: SelectionLike): boolean {
  return node.contains(selection.anchorNode) && node.contains(selection.focusNode);
}

export function createSelectionWithPoint(point: Point): SelectionLike | undefined {
  return createSelectionWithPoints({ anchorPoint: point, focusPoint: point });
}

export function createSelectionWithPoints({
  anchorPoint,
  focusPoint,
}: PointSelection): SelectionLike | undefined {
  const anchorOffsetInContainer = anchorPoint.getOffsetInContainer();
  const focusOffsetInContainer = focusPoint.getOffsetInContainer();
  if (!anchorOffsetInContainer || !focusOffsetInContainer) {
    return undefined;
  }

  const [anchorNode, anchorOffset] = anchorOffsetInContainer;
  const [focusNode, focusOffset] = focusOffsetInContainer;
  return { anchorNode, anchorOffset, focusNode, focusOffset };
}

export function createSelectionWithRange(range: Range, isBackward = false): SelectionLike {
  const anchorNode = isBackward ? range.endContainer : range.startContainer;
  const anchorOffset = isBackward ? range.endOffset : range.startOffset;

  const focusNode = isBackward ? range.startContainer : range.endContainer;
  const focusOffset = isBackward ? range.startOffset : range.endOffset;

  return { anchorNode, anchorOffset, focusNode, focusOffset };
}

export function createPointsWithSelection(selection: SelectionLike): PointSelection {
  const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  return {
    anchorPoint: createPointWithOffsetInContainer(anchorNode, anchorOffset),
    focusPoint: createPointWithOffsetInContainer(focusNode, focusOffset),
  };
}

/**
 * Returns information about the active selection within the given element.
 *
 * This is helpful for restoring the active selection later, such as after manipulating the DOM.
 */
export function createPointsWithSelectionInElement(element: Element): PointSelection | undefined {
  const selection = element.ownerDocument && element.ownerDocument.getSelection();
  if (!isSelectionPresent(selection) || !isSelectionContainedByNode(element, selection)) {
    return undefined;
  }

  return createPointsWithSelection(selection);
}

export function updateSelection(selection: Selection, newSelection: SelectionLike): void {
  const { anchorNode, anchorOffset, focusNode, focusOffset } = newSelection;

  if (selection.anchorNode !== anchorNode || selection.anchorOffset !== anchorOffset) {
    selection.collapse(anchorNode, anchorOffset);
  }

  if (selection.focusNode !== focusNode || selection.focusOffset !== focusOffset) {
    selection.extend(focusNode, focusOffset);
  }
}

/**
 * Restores the selection within the given element, if possible.
 */
export function updateSelectionInElementWithPoints(
  element: Element,
  pointSelection: PointSelection | undefined,
): void {
  if (!pointSelection) {
    return;
  }

  const selection = element.ownerDocument && element.ownerDocument.getSelection();
  if (!selection) {
    return;
  }

  const { anchorPoint, focusPoint } = pointSelection;
  try {
    updateSelectionWithPoints(selection, anchorPoint, focusPoint);
  } catch {
    // The DOM has been modified too much.
  }
}

export function updateSelectionWithRange(selection: Selection, range: Range, isBackward = false): void {
  updateSelection(selection, createSelectionWithRange(range, isBackward));
}

/** Changes <td>[Hello</td><td>]world</td> to <td>[Hello]</td><td>world</td>. */
export function rangeByMovingFromStartToPreviousEndOfPhrasingContent(range: Range): Range {
  const { commonAncestorContainer } = range;
  const phrasingContentContainer = findAncestor(
    range.endContainer,
    node => !isPhrasingContent(node),
    commonAncestorContainer,
  );
  if (!phrasingContentContainer) {
    return range;
  }

  const isAtStartOfPhrasingContent = createPointWithEndOfRange(range).isAtStartOfNode(
    phrasingContentContainer,
    node => !node.previousSibling,
    commonAncestorContainer,
  );
  if (!isAtStartOfPhrasingContent) {
    return range;
  }

  let node: Node | null = findPreviousSiblingOrAuncle(phrasingContentContainer, commonAncestorContainer);
  while (node && !isPhrasingContent(node)) {
    node = findPreviousChildOrSibling(node, commonAncestorContainer);
  }

  if (!node || !isPhrasingContent(node)) {
    return range;
  }

  const startPoint = createPointWithStartOfRange(range);
  const endOfPhrasingContentPoint = createPointAtEndOfNode(node);
  if (!endOfPhrasingContentPoint.isAfterPoint(startPoint)) {
    return range;
  }

  const newRange = range.cloneRange();
  setEndOfRangeWithPoint(newRange, endOfPhrasingContentPoint);
  return newRange;
}

export function collapsedRangeByDeletingContents(range: Range): Range {
  if (range.collapsed) {
    return range;
  }

  let collapsedRange = range.cloneRange();
  collapsedRange = rangeByMovingFromStartToPreviousEndOfPhrasingContent(collapsedRange);
  collapsedRange.deleteContents();
  return collapsedRange;
}
